import { clamp } from "./ShareCardLayout";

export const DEFAULT_CARD_LIGHTING = {
  enabled: false, lift: 0.055, colorStrength: 0.8, backgroundStrength: 1,
  cardBlendMode: "soft-light", backgroundBlendMode: "soft-light",
};
export const LIGHT_BLEND_MODES = [
  { id: "soft-light", label: "Soft Light" },
  { id: "overlay", label: "Overlay" },
  { id: "color", label: "Color" },
  { id: "screen", label: "Screen" },
  { id: "multiply", label: "Multiply" },
  { id: "normal", label: "Normal" },
];
export const resolveLightBlendMode = (value) => LIGHT_BLEND_MODES.some((mode) => mode.id === value) ? value : "soft-light";
export const makeCardLights = () => [{
  id: `light-${Date.now()}-0`, x: 0.5, y: 0.12, height: 0.45,
  power: 1, softness: 0.1, color: "#ffd9a0",
}];

const lightRgba = (color, opacity) => {
  const hex = /^#[0-9a-f]{6}$/i.test(color) ? color : "#ffffff";
  return `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${opacity})`;
};

export const paintCardLightBackground = (context, scene, lighting, lights) => {
  const strength = clamp(lighting.backgroundStrength ?? DEFAULT_CARD_LIGHTING.backgroundStrength, 0, 1);
  if (!lighting.enabled || strength <= 0) return;
  context.save();
  const blendMode = resolveLightBlendMode(lighting.backgroundBlendMode);
  context.globalCompositeOperation = blendMode === "normal" ? "source-over" : blendMode;
  for (const light of lights) {
    if (light.power <= 0) continue;
    const radius = Math.max(scene.width, scene.height) * (0.3 + light.height * 0.3);
    const x = light.x * scene.width, y = light.y * scene.height;
    const alpha = clamp(light.power * strength * 0.16, 0, 1);
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, lightRgba(light.color, alpha));
    gradient.addColorStop(0.35, lightRgba(light.color, alpha * 0.47));
    gradient.addColorStop(1, lightRgba(light.color, 0));
    context.fillStyle = gradient;
    context.fillRect(0, 0, scene.width, scene.height);
  }
  context.restore();
};

// An isolated card layer keeps the tint inside the original alpha outline.
// Light positions are converted to the card's own coordinates, including its
// ordinary 2D rotation. No perspective or surface texture is introduced.
const blendBuffers = new WeakMap();
export const paintLitCard = (canvas, surface, scene, lighting, lights, density = 1) => {
  const { width, height, crop, image } = surface;
  const ratio = Math.min(density, 8192 / Math.max(width, height), Math.sqrt(12000000 / (width * height)));
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const context = canvas.getContext("2d");
  if (!context) return canvas;
  context.scale(canvas.width / width, canvas.height / height);
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
  const strength = clamp(lighting.colorStrength ?? DEFAULT_CARD_LIGHTING.colorStrength, 0, 1);
  if (!lighting.enabled || strength <= 0) return canvas;
  const activeLights = lights.filter((light) => light.power > 0);
  if (!activeLights.length) return canvas;
  const angle = (surface.rotation || 0) * Math.PI / 180;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const unit = Math.min(scene.width, scene.height);
  const blendMode = resolveLightBlendMode(lighting.cardBlendMode);
  let tint = context;
  let buffer;
  if (blendMode !== "normal") {
    buffer = blendBuffers.get(canvas);
    if (!buffer) { buffer = document.createElement("canvas"); blendBuffers.set(canvas, buffer); }
    buffer.width = canvas.width;
    buffer.height = canvas.height;
    tint = buffer.getContext("2d");
    if (!tint) return canvas;
    tint.drawImage(canvas, 0, 0);
    tint.scale(canvas.width / width, canvas.height / height);
    tint.globalCompositeOperation = blendMode;
  } else context.globalCompositeOperation = "source-atop";
  for (const light of activeLights) {
    const dx = light.x * scene.width - surface.x, dy = light.y * scene.height - surface.y;
    const z = Math.max(0.05, light.height - lighting.lift) * unit;
    const incidence = z / Math.hypot(dx, dy, z);
    const alpha = clamp(strength * light.power * incidence * 0.42 / Math.sqrt(activeLights.length), 0, 0.65);
    const x = width / 2 + dx * cos + dy * sin;
    const y = height / 2 - dx * sin + dy * cos;
    const gradient = tint.createRadialGradient(x, y, 0, x, y, z * 2.4);
    gradient.addColorStop(0, lightRgba(light.color, alpha));
    gradient.addColorStop(0.5, lightRgba(light.color, alpha * 0.65));
    gradient.addColorStop(1, lightRgba(light.color, 0));
    tint.fillStyle = gradient;
    tint.fillRect(0, 0, width, height);
  }
  if (buffer) {
    // Blend on an isolated copy, then apply it through the original alpha.
    // This preserves antialiased card edges without pixel scans or hard masks.
    context.globalCompositeOperation = "source-atop";
    context.drawImage(buffer, 0, 0, width, height);
  }
  return canvas;
};

// Project a parallel, lifted card onto the board from a point light. The card
// itself stays flat: only its alpha silhouette is enlarged and displaced.
export const getCardLightShadow = (surface, scene, lighting, light) => {
  const unit = Math.min(scene.width, scene.height);
  const lift = clamp(Number(lighting.lift) || 0, 0, 0.2) * unit;
  const height = Math.max(0.25, Number(light.height) || 0.85) * unit;
  const factor = height / Math.max(height - lift, unit * 0.05);
  const softness = clamp(Number(light.softness) || 0, 0, 1);
  return {
    x: light.x * scene.width + (surface.x - light.x * scene.width) * factor,
    y: light.y * scene.height + (surface.y - light.y * scene.height) * factor,
    width: surface.width * factor, height: surface.height * factor,
    rotation: surface.rotation || 0,
    blur: lift * (0.04 + softness * 0.65) / Math.max(0.5, light.height),
    factor,
  };
};

const silhouettes = new WeakMap();
const getSilhouette = (surface, color) => {
  let cache = silhouettes.get(surface.image);
  if (!cache) { cache = new Map(); silhouettes.set(surface.image, cache); }
  const { crop } = surface;
  const key = `${crop.x}:${crop.y}:${crop.width}:${crop.height}:${color}`;
  if (cache.has(key)) return cache.get(key);
  const canvas = document.createElement("canvas");
  const ratio = Math.min(1, 1200 / Math.max(crop.width, crop.height));
  canvas.width = Math.max(1, Math.round(crop.width * ratio));
  canvas.height = Math.max(1, Math.round(crop.height * ratio));
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(surface.image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = "source-in";
  context.fillStyle = color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  if (cache.size >= 8) cache.delete(cache.keys().next().value);
  cache.set(key, canvas);
  return canvas;
};

export const paintCardLightShadows = (context, surfaces, scene, lighting, lights, shadow) => {
  if (!lighting.enabled || !shadow.enabled || shadow.opacity <= 0) return;
  const activeLights = lights.filter((light) => light.power > 0);
  if (!activeLights.length) return;
  const transform = context.getTransform();
  const density = Math.hypot(transform.a, transform.b) || 1;
  const buffer = document.createElement("canvas");
  context.save();
  for (const light of activeLights) {
    context.globalAlpha = clamp(shadow.opacity * light.power / Math.pow(activeLights.length, 0.6), 0, 1);
    for (const surface of surfaces) {
      if (!surface.image) continue;
      const silhouette = getSilhouette(surface, shadow.color);
      if (!silhouette) continue;
      const projection = getCardLightShadow(surface, scene, lighting, light);
      const blur = projection.blur + shadow.blur * scene.width / 1600 * 0.2;
      const radians = projection.rotation * Math.PI / 180;
      const cos = Math.abs(Math.cos(radians)), sin = Math.abs(Math.sin(radians));
      const halfWidth = (projection.width * cos + projection.height * sin) / 2 + blur * 3 + 2 / density;
      const halfHeight = (projection.height * cos + projection.width * sin) / 2 + blur * 3 + 2 / density;
      const left = Math.max(0, projection.x - halfWidth), top = Math.max(0, projection.y - halfHeight);
      const width = Math.min(scene.width, projection.x + halfWidth) - left;
      const height = Math.min(scene.height, projection.y + halfHeight) - top;
      if (width <= 0 || height <= 0) continue;
      // Rotate the silhouette before blurring. Transparent margins prevent a
      // blur from sampling the image rectangle's edges as an opaque border.
      buffer.width = Math.max(1, Math.ceil(width * density));
      buffer.height = Math.max(1, Math.ceil(height * density));
      const part = buffer.getContext("2d");
      if (!part) continue;
      part.setTransform(buffer.width / width, 0, 0, buffer.height / height, -left * buffer.width / width, -top * buffer.height / height);
      part.translate(projection.x, projection.y);
      part.rotate(radians);
      part.drawImage(silhouette, -projection.width / 2, -projection.height / 2, projection.width, projection.height);
      // Canvas filter lengths use backing pixels rather than scene units.
      context.filter = blur > 0 ? `blur(${blur * density}px)` : "none";
      context.drawImage(buffer, left, top, width, height);
    }
  }
  context.restore();
};
